<template>
  <div class="page-container" v-wechat-title="titleTop">
    
    <!-- 1. 全新设计的“全对”状态 -->
    <div v-if="!loading && selectQuestionList.length === 0" class="all-correct-container">
      <div class="all-correct-card">
        <!-- Shield-Check Icon with Glow -->
        <div class="icon-wrapper">
          <svg class="icon-svg" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M466.5 83.7l-192-80a48.15 48.15 0 00-36.9 0l-192 80C27.7 93.7 16 115.8 16 140.1V284c0 120.4 121.2 216.9 234.4 242.3a48.28 48.28 0 0011.2 0C374.8 500.9 496 404.4 496 284V140.1c0-24.3-11.7-46.4-29.5-56.4z" fill="#0057FF" fill-opacity="0.1" stroke="#0057FF" stroke-width="16"/>
            <path d="M346.6 172.6a12.02 12.02 0 0117 17l-128 128a12 12 0 01-17 0l-72-72a12 12 0 0117-17L224 286.1l122.6-113.5z" fill="#0057FF"/>
          </svg>
        </div>
        <h1 class="all-correct-title">本次测验全部正确</h1>
        <p class="all-correct-subtitle">您已掌握本章节所有知识点，未发现错题记录。</p>
        <button class="return-btn" @click="onBack">返回上一页</button>
      </div>
    </div>

    <!-- 2. 主界面 (有错题时显示) -->
    <template v-else-if="questionData">
      
      <!-- STICKY HEADER -->
      <header class="sticky-header">
        <div class="header-inner">
          <div class="header-left">
            <a class="back-btn" @click="onBack">
              <!-- Chevron Left Icon -->
              <svg viewBox="0 0 320 512" width="10" height="16" fill="currentColor">
                <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
              </svg>
            </a>
            <h1 class="header-title">{{ title }}</h1>
          </div>
          <div class="header-right">
            <span class="page-current">{{ questionIndex + 1 }}</span>
            <span class="page-total">/{{ selectQuestionList.length }}</span>
          </div>
        </div>
        <!-- Progress Bar -->
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </header>

      <!-- MAIN CONTENT -->
      <main class="main-body">
        <transition :name="transitionName" mode="out-in">
          <div :key="questionData.id" class="slide-item">
            
            <!-- Question Card -->
            <div class="question-card">
              <div v-if="!isCurrentQuestionCorrect" class="badge-wrong">回答错误</div>
              <span class="q-type">{{ questionData.isMultiple ? '多选题' : '单选题' }}</span>
              <h1 class="q-text" v-html="questionData.question"></h1>
            </div>

            <!-- Options List -->
            <div class="options-list">
              <div 
                v-for="answer in questionData.answer" 
                :key="answer.answerId"
                class="option-card"
                :class="getOptionClass(answer)"
              >
                <!-- Icon Circle -->
                <div class="opt-circle">
                  <!-- Check Icon (Correct) -->
                  <svg v-if="answer.isRight" viewBox="0 0 448 512" width="10" height="10" fill="currentColor"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>
                  <!-- Times Icon (Wrong) -->
                  <svg v-else-if="answer.isSelect && !answer.isRight" viewBox="0 0 320 512" width="10" height="10" fill="currentColor"><path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"/></svg>
                </div>
                
                <span class="opt-text">{{ answer.body }}</span>
                
                <!-- Badge -->
                <span v-if="answer.isRight" class="opt-badge badge-green">正确答案</span>
                <span v-else-if="answer.isSelect" class="opt-badge badge-red">您的选择</span>
              </div>
            </div>

            <!-- Analysis Box -->
            <div v-if="questionData.analysis" class="analysis-box">
              <div class="analysis-header">
                <div class="bulb-icon">
                  <svg viewBox="0 0 352 512" width="12" height="12" fill="currentColor"><path d="M96.06 454.35c.01 6.29 1.87 12.45 5.36 17.69l17.09 25.69a31.99 31.99 0 0 0 26.64 14.28h61.71a31.99 31.99 0 0 0 26.64-14.28l17.09-25.69a31.989 31.989 0 0 0 5.36-17.69l.04-38.35H96.01l.05 38.35zM0 176c0 44.37 16.45 84.85 43.56 115.78 16.52 18.85 42.36 58.23 52.21 91.45.04.15.07.3.11.45h160.24c.04-.15.07-.3.11-.45 9.85-33.22 35.69-72.6 52.21-91.45C335.55 260.85 352 220.37 352 176 352 78.61 272.91-.3 175.45 0 73.44.31 0 82.97 0 176zm176-80c-44.11 0-80 35.89-80 80 0 8.84-7.16 16-16 16s-16-7.16-16-16c0-61.76 50.24-112 112-112 8.84 0 16 7.16 16 16s-7.16 16-16 16z"/></svg>
                </div>
                <h3 class="analysis-title">题目解析</h3>
              </div>
              <p class="analysis-text">
                <strong class="text-blue">正确解析：</strong>{{ questionData.analysis }}
              </p>
            </div>

          </div>
        </transition>
      </main>

      <!-- FIXED FOOTER -->
      <footer class="sticky-footer">
        <div class="footer-inner">
          <!-- Prev -->
          <button 
            class="btn btn-prev" 
            :disabled="questionIndex <= 0" 
            @click="onUpQuestion"
          >
            上一题
          </button>

          <!-- Next / Finish -->
          <button 
            class="btn btn-next" 
            @click="onDownQuestion"
          >
            <template v-if="isLastQuestion">
              完成回顾 
              <svg viewBox="0 0 448 512" width="12" height="12" fill="currentColor" style="margin-left:6px"><path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/></svg>
            </template>
            <template v-else>
              下一题 
              <svg viewBox="0 0 320 512" width="12" height="12" fill="currentColor" style="margin-left:6px"><path d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"/></svg>
            </template>
          </button>
        </div>
      </footer>

    </template>
  </div>
</template>

<script>
import request from '@/utils/request'
import { GetDiveTitle } from '@/utils/base'

export default {
  name: 'ExamErrorReview',
  components: {
    // 移除 fullError 依赖
  },
  data() {
    return {
      titleTop: '',
      title: '错题回顾',
      id: undefined,
      data: { baseData: undefined, questionList: [] },
      selectQuestionList: [],
      questionData: undefined,
      questionIndex: 0,
      loading: true,
      transitionName: 'slide-right'
    }
  },
  computed: {
    progressPercent() {
      const total = this.selectQuestionList.length
      if (!total) return 0
      return ((this.questionIndex + 1) / total) * 100
    },
    isLastQuestion() {
      return this.selectQuestionList.length > 0 && this.questionIndex === this.selectQuestionList.length - 1
    },
    isCurrentQuestionCorrect() {
      return this.questionData && this.questionData.isRight
    }
  },
  created() {
    this.titleTop = GetDiveTitle()
    this.id = this.$route.params.id
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      request({
        url: '/DiveLearn/getExamError',
        method: 'post',
        params: { id: this.id }
      }).then(response => {
        this.loading = false
        if (!response.success) return
        
        this.data = response.data
        let i = 1
        this.data.questionList.forEach(item => { item.id = i++ })
        
        this.selectQuestionList = this.data.questionList.filter(item => item.isRight === false)
        
        if (this.selectQuestionList.length > 0) {
          this.questionIndex = 0
          this.onLoadQuestion()
        }
      })
    },
    onLoadQuestion() {
      this.questionData = this.selectQuestionList[this.questionIndex]
    },
    onUpQuestion() {
      if (this.questionIndex <= 0) return
      this.transitionName = 'slide-left'
      this.questionIndex--
      this.onLoadQuestion()
    },
    onDownQuestion() {
      if (this.isLastQuestion) {
        this.$router.go(-1)
        return
      }
      if (this.questionIndex + 1 >= this.selectQuestionList.length) return
      this.transitionName = 'slide-right'
      this.questionIndex++
      this.onLoadQuestion()
    },
    onBack() {
      this.$router.go(-1)
    },
    getOptionClass(answer) {
      if (answer.isRight) return 'option-correct'
      if (answer.isSelect && !answer.isRight) return 'option-wrong'
      return 'option-normal'
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

* {
  box-sizing: border-box;
}

.page-container {
  min-height: 100vh;
  background-color: #F9FAFB;
  font-family: 'Inter', -apple-system, sans-serif;
  color: #0A2540;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}

/* --- 全对状态页样式 --- */
.all-correct-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}
.all-correct-card {
  max-width: 24rem;
  width: 100%;
}
.icon-wrapper {
  margin-bottom: 2rem;
  display: inline-block;
  position: relative;
}
.icon-wrapper .icon-svg {
  width: 4rem; /* 64px */
  height: 4rem;
  position: relative;
  z-index: 2;
}
.icon-wrapper::before {
  content: '';
  position: absolute;
  inset: -1rem;
  background: radial-gradient(circle, rgba(0, 87, 255, 0.2), transparent 70%);
  animation: pulse 2s infinite;
  z-index: 1;
}
@keyframes pulse {
  0% { transform: scale(0.9); opacity: 0.7; }
  70% { transform: scale(1.1); opacity: 0; }
  100% { transform: scale(0.9); opacity: 0; }
}
.all-correct-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}
.all-correct-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 2.5rem 0;
}
.return-btn {
  width: 100%;
  background: #0A2540;
  color: white;
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}
.return-btn:active { transform: scale(0.98); }

/* --- 错题回顾主样式 --- */
.page-container {
  padding-bottom: 96px; /* Footer space */
}
.sticky-header {
  background: white;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 0;
  z-index: 40;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  width: 100%;
}
.header-inner {
  max-width: 48rem;
  margin: 0 auto;
  height: 3.5rem;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-left { display: flex; align-items: center; gap: 1rem; }
.back-btn { color: #9CA3AF; cursor: pointer; display: flex; align-items: center; }
.back-btn:hover { color: #0A2540; }
.header-title { font-size: 1rem; font-weight: 700; color: #0A2540; margin: 0; }

.header-right { font-size: 0.875rem; font-weight: 700; color: #6B7280; }
.page-current { color: #0057FF; font-size: 1.125rem; }
.page-total { color: #D1D5DB; font-weight: 400; }

.progress-track { height: 4px; width: 100%; background: #F3F4F6; }
.progress-fill { height: 4px; background: #EF4444; transition: width 0.3s ease; }

.main-body {
  flex: 1;
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
  padding: 1rem;
  position: relative;
  overflow-x: hidden;
}

.slide-right-enter-active, .slide-right-leave-active,
.slide-left-enter-active, .slide-left-leave-active {
  transition: all 0.3s ease;
  width: 100%;
}
.slide-right-enter { transform: translateX(20px); opacity: 0; }
.slide-right-leave-to { transform: translateX(-20px); opacity: 0; }
.slide-left-enter { transform: translateX(-20px); opacity: 0; }
.slide-left-leave-to { transform: translateX(20px); opacity: 0; }

.question-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  border: 1px solid #F3F4F6;
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
  margin-bottom: 1.5rem;
}
.badge-wrong {
  position: absolute;
  top: 0; right: 0;
  background: #FEF2F2;
  color: #EF4444;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-bottom-left-radius: 0.75rem;
  border-left: 1px solid #FEE2E2;
  border-bottom: 1px solid #FEE2E2;
}
.q-type {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  letter-spacing: 0.05em;
}
.q-text {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0A2540;
  line-height: 1.6;
  margin: 0;
}

.options-list { display: grid; gap: 0.75rem; }
.option-card {
  border: 2px solid #E5E7EB;
  background: white;
  padding: 1rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s;
}
.opt-circle {
  width: 1.5rem; height: 1.5rem;
  border-radius: 999px;
  border: 2px solid #D1D5DB;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.opt-text { color: #4B5563; font-weight: 500; flex: 1; line-height: 1.4; }
.opt-badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
}

.option-correct { border-color: #10B981; background-color: #ECFDF5; }
.option-correct .opt-circle { background-color: #10B981; border-color: #10B981; color: white; }
.option-correct .opt-text { color: #065F46; font-weight: 600; }
.badge-green { background: rgba(255,255,255,0.6); color: #059669; }

.option-wrong { border-color: #EF4444; background-color: #FEF2F2; }
.option-wrong .opt-circle { background-color: #EF4444; border-color: #EF4444; color: white; }
.option-wrong .opt-text { color: #991B1B; font-weight: 600; }
.badge-red { background: rgba(255,255,255,0.6); color: #DC2626; }

.analysis-box {
  margin-top: 1.5rem;
  background-color: #EFF6FF;
  border: 1px solid #DBEAFE;
  border-radius: 1rem;
  padding: 1.25rem;
}
.analysis-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; }
.bulb-icon {
  width: 1.5rem; height: 1.5rem;
  background-color: #0057FF;
  color: white;
  border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
}
.analysis-title { font-size: 0.875rem; font-weight: 700; color: #0A2540; margin: 0; }
.analysis-text { font-size: 0.875rem; color: #4B5563; line-height: 1.6; text-align: justify; margin: 0; }
.text-blue { color: #0057FF; }

.sticky-footer {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  background: white;
  border-top: 1px solid #E5E7EB;
  padding: 1rem;
  z-index: 40;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.05);
}
.footer-inner {
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
  gap: 1rem;
}
.btn {
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.75rem;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
}
.btn-prev {
  background: #F3F4F6;
  color: #9CA3AF;
  border: 1px solid #E5E7EB;
}
.btn-prev:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-next {
  background: #0A2540;
  color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.btn-next:active { transform: scale(0.98); }

</style>